import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

/**
 * 地点の操作の拒否理由 (backend の `CustomerSupplyPointErrorReason` に対応)。
 * RPC をつなぐ時は ErrorInfo の reason からこの型へ写像し、 画面側の対応表はそのまま使う。
 */
export type SupplyPointErrorReason =
  | 'CUSTOMER_REQUIRED'
  | 'CUSTOMER_NAME_REQUIRED'
  | 'CUSTOMER_NAME_TOO_LONG'
  | 'CONTRACT_KW_NOT_POSITIVE'
  | 'END_BEFORE_START'
  | 'SUPPLY_POINT_NUMBER_REQUIRED'
  | 'SUPPLY_POINT_NUMBER_INVALID'
  | 'SUPPLY_POINT_NUMBER_IMMUTABLE'
  | 'PERIOD_OVERLAP'
  | 'CONTRACTOR_REQUIRED'
  | 'CUSTOMER_AREA_MISMATCH'
  | 'CUSTOMER_NUMBER_MISMATCH'
  | 'SUPPLY_POINT_NUMBER_NOT_EDITABLE'
  | 'SUPPLY_POINT_NUMBER_AREA_MISMATCH'
  | 'KW_PERIOD_INVALID'
  | 'KW_PERIODS_TOO_MANY'
  | 'RETAILER_REQUIRED'
  | 'RETAILER_NOT_IN_AREA'
  | 'RETAILER_NOT_CHANGEABLE'
  | 'RETAILER_UNAVAILABLE_IN_AREA'
  | 'PRIVATE_GENERATION_CAPACITY_REQUIRED'
  | 'REVERSE_POWER_FLOW_REQUIRED'
  | 'REVERSE_POWER_FLOW_OUT_OF_RANGE'
  | 'STAGE_TRANSITION_NOT_ALLOWED'
  | 'CONTRACT_IN_USE'
  | 'ACTUALS_CHECK_UNAVAILABLE'
  | 'CONFLICT'

/** 文言に差し込む値 (ErrorInfo の metadata 相当)。 */
export type SupplyPointErrorParams = {
  supplyPointNumber?: string
  areaCode?: AreaCode
  /** 需要家の名前 (エリア・番号が合わない時の文言に入れる)。 */
  customerName?: string
  /** 番号が未定の見込みを、番号のある需要家に足そうとした。 */
  numberBlank?: boolean
  /** エリア・番号を直したために、 同じ需要家の他の契約と食い違った。 */
  fixOthers?: boolean
}

export class SupplyPointError extends Error {
  readonly reason: SupplyPointErrorReason
  readonly params: SupplyPointErrorParams

  constructor(
    reason: SupplyPointErrorReason,
    params: SupplyPointErrorParams = {},
  ) {
    super(reason)
    this.name = 'SupplyPointError'
    this.reason = reason
    this.params = params
  }
}

/** エラーを欄の下に出すか (入力保持)、 モーダル上部に出すか。 */
export type SupplyPointErrorField =
  | 'customer'
  | 'customerName'
  | 'contractor'
  | 'contractKw'
  | 'supplyEndDate'
  | 'supplyPointNumber'
  | 'retailer'
  | 'privateGenerationCapacityKw'
  | 'reversePowerFlowContractKw'
  | 'root'

export const SUPPLY_POINT_ERROR_FIELD: Record<
  SupplyPointErrorReason,
  SupplyPointErrorField
> = {
  CUSTOMER_REQUIRED: 'customer',
  CUSTOMER_NAME_REQUIRED: 'customerName',
  CUSTOMER_NAME_TOO_LONG: 'customerName',
  CONTRACT_KW_NOT_POSITIVE: 'contractKw',
  END_BEFORE_START: 'supplyEndDate',
  SUPPLY_POINT_NUMBER_REQUIRED: 'supplyPointNumber',
  SUPPLY_POINT_NUMBER_INVALID: 'supplyPointNumber',
  SUPPLY_POINT_NUMBER_IMMUTABLE: 'supplyPointNumber',
  PERIOD_OVERLAP: 'supplyPointNumber',
  CONTRACTOR_REQUIRED: 'contractor',
  CUSTOMER_AREA_MISMATCH: 'root',
  CUSTOMER_NUMBER_MISMATCH: 'root',
  SUPPLY_POINT_NUMBER_NOT_EDITABLE: 'supplyPointNumber',
  SUPPLY_POINT_NUMBER_AREA_MISMATCH: 'supplyPointNumber',
  KW_PERIOD_INVALID: 'contractKw',
  KW_PERIODS_TOO_MANY: 'contractKw',
  RETAILER_REQUIRED: 'retailer',
  RETAILER_NOT_IN_AREA: 'retailer',
  RETAILER_NOT_CHANGEABLE: 'retailer',
  RETAILER_UNAVAILABLE_IN_AREA: 'retailer',
  PRIVATE_GENERATION_CAPACITY_REQUIRED: 'privateGenerationCapacityKw',
  REVERSE_POWER_FLOW_REQUIRED: 'reversePowerFlowContractKw',
  REVERSE_POWER_FLOW_OUT_OF_RANGE: 'reversePowerFlowContractKw',
  STAGE_TRANSITION_NOT_ALLOWED: 'root',
  CONTRACT_IN_USE: 'root',
  ACTUALS_CHECK_UNAVAILABLE: 'root',
  CONFLICT: 'root',
}

export const SUPPLY_POINT_ERROR_I18N_KEY: Record<
  SupplyPointErrorReason,
  string
> = {
  CUSTOMER_REQUIRED: 'page:customerInfo.error.customerRequired',
  CUSTOMER_NAME_REQUIRED: 'page:customerInfo.error.customerNameRequired',
  CUSTOMER_NAME_TOO_LONG: 'page:customerInfo.error.customerNameTooLong',
  CONTRACT_KW_NOT_POSITIVE: 'page:customerInfo.error.contractKwNotPositive',
  END_BEFORE_START: 'page:customerInfo.error.endBeforeStart',
  SUPPLY_POINT_NUMBER_REQUIRED:
    'page:customerInfo.error.supplyPointNumberRequired',
  SUPPLY_POINT_NUMBER_INVALID:
    'page:customerInfo.error.supplyPointNumberInvalid',
  SUPPLY_POINT_NUMBER_IMMUTABLE:
    'page:customerInfo.error.supplyPointNumberImmutable',
  PERIOD_OVERLAP: 'page:customerInfo.error.periodOverlap',
  CONTRACTOR_REQUIRED: 'page:customerInfo.error.contractorRequired',
  CUSTOMER_AREA_MISMATCH: 'page:customerInfo.error.customerAreaMismatch',
  CUSTOMER_NUMBER_MISMATCH: 'page:customerInfo.error.customerNumberMismatch',
  SUPPLY_POINT_NUMBER_NOT_EDITABLE:
    'page:customerInfo.error.supplyPointNumberNotEditable',
  SUPPLY_POINT_NUMBER_AREA_MISMATCH:
    'page:customerInfo.error.supplyPointNumberAreaMismatch',
  KW_PERIOD_INVALID: 'page:customerInfo.error.kwPeriodInvalid',
  KW_PERIODS_TOO_MANY: 'page:customerInfo.error.kwPeriodsTooMany',
  RETAILER_REQUIRED: 'page:customerInfo.error.retailerRequired',
  RETAILER_NOT_IN_AREA: 'page:customerInfo.error.retailerNotInArea',
  RETAILER_NOT_CHANGEABLE: 'page:customerInfo.error.retailerNotChangeable',
  RETAILER_UNAVAILABLE_IN_AREA:
    'page:customerInfo.error.retailerUnavailableInArea',
  PRIVATE_GENERATION_CAPACITY_REQUIRED:
    'page:customerInfo.error.privateGenerationCapacityRequired',
  REVERSE_POWER_FLOW_REQUIRED:
    'page:customerInfo.error.reversePowerFlowRequired',
  REVERSE_POWER_FLOW_OUT_OF_RANGE:
    'page:customerInfo.error.reversePowerFlowOutOfRange',
  STAGE_TRANSITION_NOT_ALLOWED:
    'page:customerInfo.error.stageTransitionNotAllowed',
  CONTRACT_IN_USE: 'page:customerInfo.error.contractInUse',
  ACTUALS_CHECK_UNAVAILABLE: 'page:customerInfo.error.actualsCheckUnavailable',
  CONFLICT: 'page:customerInfo.error.conflict',
}

/** 契約者の保存・削除の拒否理由 (モックの `ValidateContractor` / `ContractorDeleteBlockReason`)。 */
export type ContractorErrorReason =
  | 'NAME_REQUIRED'
  | 'NAME_TOO_LONG'
  | 'POSTAL_CODE_TOO_LONG'
  | 'ADDRESS_TOO_LONG'
  | 'ATTENTION_TOO_LONG'
  | 'NOTE_TOO_LONG'
  | 'HAS_CUSTOMERS'

export class ContractorError extends Error {
  readonly reason: ContractorErrorReason
  readonly params: { customerCount?: number }

  constructor(
    reason: ContractorErrorReason,
    params: { customerCount?: number } = {},
  ) {
    super(reason)
    this.name = 'ContractorError'
    this.reason = reason
    this.params = params
  }
}

export const CONTRACTOR_ERROR_I18N_KEY: Record<ContractorErrorReason, string> =
  {
    NAME_REQUIRED: 'page:customerInfo.contractors.error.nameRequired',
    NAME_TOO_LONG: 'page:customerInfo.contractors.error.nameTooLong',
    POSTAL_CODE_TOO_LONG:
      'page:customerInfo.contractors.error.postalCodeTooLong',
    ADDRESS_TOO_LONG: 'page:customerInfo.contractors.error.addressTooLong',
    ATTENTION_TOO_LONG: 'page:customerInfo.contractors.error.attentionTooLong',
    NOTE_TOO_LONG: 'page:customerInfo.contractors.error.noteTooLong',
    HAS_CUSTOMERS: 'page:customerInfo.contractors.deleteBlocked',
  }
